import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import { WIDGET_DISPLAY_CHOICE } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { StyledCard } from '@/pages/GeneralSetting/styled';
import { generalSettingSelector, handleSaveSettings } from '@/redux/slice/setting.slice';
import { Badge, BlockStack, Box, Card, Divider, InlineStack, Link, useBreakpoints } from '@shopify/polaris';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export const AutomaticBlocking = () => {
  const { t } = useTranslation('general_settings');
  const generalSettings = useSelector(generalSettingSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { mdUp } = useBreakpoints();

  const isOnlyShowCartPage = useMemo(
    () => generalSettings.displayOnPage.length === 1 && generalSettings.displayOnPage.includes(WIDGET_DISPLAY_CHOICE.CART),
    [generalSettings.displayOnPage],
  );

  const isOnlyShowProductPage = useMemo(
    () => generalSettings.displayOnPage.length === 1 && generalSettings.displayOnPage.includes(WIDGET_DISPLAY_CHOICE.PRODUCT),
    [generalSettings.displayOnPage],
  );

  const isShowBothPage = useMemo(() => generalSettings.displayOnPage.length === 2, [generalSettings.displayOnPage.length]);

  return (
    <Card padding={'300'}>
      <BlockStack>
        <Box>
          <BoldText>{t('sections.checkoutValidation.autoBlock.title')}</BoldText>
          <RegularText tone="subdued">{t('sections.checkoutValidation.autoBlock.helpText')}</RegularText>
        </Box>
        <StyledCard>
          <Card>
            <BlockStack gap={'100'}>
              <InlineStack align="space-between">
                <BoldText as="p" fontWeight="medium" tone={isOnlyShowProductPage ? 'subdued' : undefined}>
                  {t('sections.checkoutValidation.autoBlock.cartTitle')}
                </BoldText>
                <Switch
                  isActive={generalSettings.isDisableCheckoutButton}
                  onSwitch={() =>
                    dispatch(handleSaveSettings({ isDisableCheckoutButton: !generalSettings.isDisableCheckoutButton }))
                  }
                  isDisabled={isOnlyShowProductPage}
                />
              </InlineStack>
              {isOnlyShowProductPage && (
                <Box paddingBlockEnd={'100'}>
                  <RegularText tone="subdued">
                    This feature is only available for widget displayed on{' '}
                    <Link onClick={() => navigate(PATH.WIDGET.pathname)} removeUnderline>
                      only Cart page
                    </Link>
                  </RegularText>
                </Box>
              )}
              <Box maxWidth="466px" padding={'0'}>
                <RegularText as="span" tone={isOnlyShowProductPage ? 'subdued' : undefined}>
                  Disable <BoldSmallText as="span">checkout button</BoldSmallText> if no date is selected.{' '}
                  <BoldSmallText as="span">Buy now button</BoldSmallText> in product page and{' '}
                  <BoldSmallText as="span">Direct method (ex. Paypal, Apple Pay,...)</BoldSmallText> will also be disabled.
                </RegularText>
              </Box>
            </BlockStack>
            <Box paddingBlockEnd={'300'} paddingBlockStart={'300'}>
              <Divider borderColor="border" />
            </Box>
            <BlockStack gap={'100'}>
              {mdUp ? (
                <BlockStack gap={'100'}>
                  <InlineStack align="space-between">
                    <InlineStack gap={'200'}>
                      <BoldText as="p" fontWeight="medium" tone={isOnlyShowCartPage || isShowBothPage ? 'subdued' : undefined}>
                        {t('sections.checkoutValidation.autoBlock.productTitle')}
                      </BoldText>
                      <Box>
                        <Badge tone="info">💡 Recommended for only product page</Badge>
                      </Box>
                    </InlineStack>
                    <Switch
                      isActive={generalSettings.isDisableAddToCartButton}
                      onSwitch={() =>
                        dispatch(handleSaveSettings({ isDisableAddToCartButton: !generalSettings.isDisableAddToCartButton }))
                      }
                      isDisabled={isOnlyShowCartPage || isShowBothPage}
                    />
                  </InlineStack>
                  {(isOnlyShowCartPage || isShowBothPage) && (
                    <Box paddingBlockEnd={'100'}>
                      <RegularText tone="subdued">
                        This feature is only available for widget displayed on{' '}
                        <Link onClick={() => navigate(PATH.WIDGET.pathname)} removeUnderline>
                          only Product page
                        </Link>
                      </RegularText>
                    </Box>
                  )}
                </BlockStack>
              ) : (
                <BlockStack gap={'100'}>
                  <InlineStack align="space-between">
                    <BoldText as="p" fontWeight="medium">
                      {t('sections.checkoutValidation.autoBlock.productTitle')}
                    </BoldText>
                    <Switch
                      isActive={generalSettings.isDisableAddToCartButton}
                      onSwitch={() =>
                        dispatch(handleSaveSettings({ isDisableAddToCartButton: !generalSettings.isDisableAddToCartButton }))
                      }
                      isDisabled={isOnlyShowCartPage || isShowBothPage}
                    />
                  </InlineStack>
                  <Box>
                    <Badge tone="info">💡 Recommended for only product page</Badge>
                  </Box>
                  {(isOnlyShowCartPage || isShowBothPage) && (
                    <Box paddingBlockEnd={'100'}>
                      <RegularText tone="subdued">
                        This feature is only available for widget displayed on{' '}
                        <Link onClick={() => navigate(PATH.WIDGET.pathname)} removeUnderline>
                          only Product page
                        </Link>
                      </RegularText>
                    </Box>
                  )}
                </BlockStack>
              )}
              <Box maxWidth="466px" padding={'0'}>
                <RegularText tone={isOnlyShowCartPage || isShowBothPage ? 'subdued' : undefined}>
                  Disable <BoldSmallText as="span">add to cart button</BoldSmallText> if no date is selected
                </RegularText>
              </Box>
            </BlockStack>
          </Card>
        </StyledCard>
      </BlockStack>
    </Card>
  );
};
